import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  savePendingAuthSession,
  getPendingAuthSession,
  clearPendingAuthSession,
} from "../lib/authSession";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, User, Lock, Phone, KeyRound, CheckCircle2, Mail, ArrowLeft, ShieldCheck, RefreshCw } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const inputCls =
  "w-full max-w-full min-w-0 box-border h-11 px-3.5 rounded-xl bg-stone-50 border border-stone-200 text-stone-900 placeholder:text-stone-400 focus:bg-white focus:outline-none focus:border-[#0a2140] focus:ring-2 focus:ring-[#0a2140]/10 transition-all text-sm";

export const checkPasswordStrength = (pwd = "") => {
  const hasMinLen = (pwd || "").length >= 6;
  const hasUpper = /[A-Z]/.test(pwd || "");
  const hasLower = /[a-z]/.test(pwd || "");
  const hasNumber = /\d/.test(pwd || "");
  const hasSymbol = /[!@#$%^&*(),.?":{}|<>\-_=+[\]\\/;~`']|[\W_]/.test(pwd || "");
  const isValid = hasMinLen && hasUpper && hasLower && hasNumber && hasSymbol;
  return { hasMinLen, hasUpper, hasLower, hasNumber, hasSymbol, isValid };
};

export const PasswordRequirements = ({ password = "", showOnlyWhenDirty = true }) => {
  if (showOnlyWhenDirty && !password) return null;
  const { hasMinLen, hasUpper, hasLower, hasNumber, hasSymbol } = checkPasswordStrength(password);

  const criteria = [
    { label: "6+ chars", met: hasMinLen },
    { label: "Uppercase (A-Z)", met: hasUpper },
    { label: "Lowercase (a-z)", met: hasLower },
    { label: "Number (0-9)", met: hasNumber },
    { label: "Symbol (!@#...)", met: hasSymbol },
  ];

  return (
    <div className="flex flex-wrap gap-1.5 pt-1.5 text-[10px] font-medium" data-testid="password-requirements-checklist">
      {criteria.map((c, idx) => (
        <span
          key={idx}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md transition-colors ${c.met
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-stone-100 text-stone-500 border border-stone-200"
            }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${c.met ? "bg-emerald-500" : "bg-stone-300"}`} />
          {c.label}
        </span>
      ))}
    </div>
  );
};

export const AuthForm = ({
  initialMode = "login",
  onSuccess,
  onSwitchToB2B,
  onSwitchToLogin,
  onSwitchToB2C,
}) => {
  // Check URL query parameters and persistent pending session
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const urlCode = (urlParams?.get("code") || "").replace(/\D/g, "").slice(0, 6);
  const urlEmail = (urlParams?.get("email") || "").trim();
  const urlStep = urlParams?.get("step") || "";

  const savedSession = getPendingAuthSession();
  const hasSavedReg = savedSession?.type === "retail_register" && Boolean(savedSession?.regOtpSent);
  const hasSavedForgot = savedSession?.type === "forgot_password" && Boolean(savedSession?.forgotOtpSent);

  const isInitialSignup = initialMode === "register" || initialMode === "b2c_register" || hasSavedReg || urlStep === "otp";

  // mode: "login" | "register" | "forgot_password"
  const [mode, setMode] = useState(() => {
    if (hasSavedForgot) return "forgot_password";
    if (isInitialSignup) return "register";
    return "login";
  });

  // 1. Sign In Form State (Only 2 inputs: identifier + password)
  const [loginForm, setLoginForm] = useState({ identifier: urlEmail || "", password: "" });
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // 2. Register Form State (First name, Last name, Phone, Email, Password, Confirm Password)
  const [regForm, setRegForm] = useState(() => {
    if (hasSavedReg && savedSession?.regForm) {
      return { ...savedSession.regForm };
    }
    return {
      firstName: "",
      lastName: "",
      phone: "",
      email: urlEmail || "",
      password: "",
      confirmPassword: "",
    };
  });
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  const [regOtpSent, setRegOtpSent] = useState(() => hasSavedReg || urlStep === "otp");
  const [regOtpChannels, setRegOtpChannels] = useState(() => savedSession?.regOtpChannels || { email: true, mobile: false });
  const [regOtpCode, setRegOtpCode] = useState(() => urlCode || "");
  const [regCountdown, setRegCountdown] = useState(() => {
    if (hasSavedReg && savedSession?.savedAt) {
      const elapsed = Math.floor((Date.now() - savedSession.savedAt) / 1000);
      return Math.max(0, 30 - elapsed);
    }
    return 0;
  });

  // 3. Forgot Password Form State
  const [forgotIdentifier, setForgotIdentifier] = useState(() => savedSession?.forgotIdentifier || urlEmail || "");
  const [forgotOtpSent, setForgotOtpSent] = useState(() => hasSavedForgot);
  const [forgotOtpCode, setForgotOtpCode] = useState(() => (hasSavedForgot ? urlCode : ""));
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
  const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
  const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
  const [forgotMaskedInfo, setForgotMaskedInfo] = useState(() => savedSession?.forgotMaskedInfo || { email: null, phone: null });
  const [forgotCountdown, setForgotCountdown] = useState(() => {
    if (hasSavedForgot && savedSession?.savedAt) {
      const elapsed = Math.floor((Date.now() - savedSession.savedAt) / 1000);
      return Math.max(0, 30 - elapsed);
    }
    return 0;
  });

  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Only override mode if no active saved session is being restored
    const session = getPendingAuthSession();
    if (session?.regOtpSent || session?.forgotOtpSent) {
      return;
    }
    const isSignup = initialMode === "register" || initialMode === "b2c_register";
    setMode(isSignup ? "register" : "login");
  }, [initialMode]);

  // Sync urlCode if received late or via email return link
  useEffect(() => {
    if (urlCode) {
      if (mode === "register" && regOtpSent) {
        setRegOtpCode(urlCode);
      } else if (mode === "forgot_password" && forgotOtpSent) {
        setForgotOtpCode(urlCode);
      }
    }
  }, [urlCode, mode, regOtpSent, forgotOtpSent]);

  // Registration OTP Countdown
  useEffect(() => {
    if (regCountdown > 0) {
      const timer = setTimeout(() => setRegCountdown(regCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [regCountdown]);

  // Forgot Password OTP Countdown
  useEffect(() => {
    if (forgotCountdown > 0) {
      const timer = setTimeout(() => setForgotCountdown(forgotCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [forgotCountdown]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setRegOtpSent(false);
    setRegOtpChannels({ email: true, mobile: false });
    setRegOtpCode("");
    setForgotOtpSent(false);
    setForgotOtpCode("");
    clearPendingAuthSession();
    if (newMode === "login" && onSwitchToLogin) onSwitchToLogin();
    if (newMode === "register" && onSwitchToB2C) onSwitchToB2C();
  };

  const onAuthSuccess = (userData) => {
    clearPendingAuthSession();
    setUser(userData);
    toast.success(userData.is_new_user ? "Welcome to Zaveri Trunk!" : "Welcome back!");
    if (onSuccess) {
      onSuccess(userData);
    } else {
      navigate(userData.role === "admin" || userData.role === "seller" || userData.role === "staff" ? "/dashboard" : "/");
    }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    const identifier = loginForm.identifier.trim();
    if (!identifier) {
      toast.error("Please enter your registered email address.");
      return;
    }
    const cleanDigits = identifier.replace(/\D/g, "");
    if (!identifier.includes("@") && cleanDigits.length >= 10) {
      toast.error("Login with mobile number is disabled. Please log in using your registered email address.");
      return;
    }
    if (!identifier.includes("@") && !["zaveri", "admin", "seller"].includes(identifier.toLowerCase())) {
      toast.error("Please enter a valid registered email address.");
      return;
    }
    if (!loginForm.password || loginForm.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        identifier: identifier,
        whatsapp: identifier,
        email: identifier,
        password: loginForm.password,
      };
      const { data } = await api.post("/auth/login", payload);
      onAuthSuccess(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // B. Handle Register: Send Dual-Channel OTP
  // -----------------------------
  const handleSendRegisterOtp = async (e) => {
    e?.preventDefault();
    if (!regForm.firstName.trim() || !regForm.lastName.trim()) {
      toast.error("Please enter both first name and last name");
      return;
    }
    const rawDigits = regForm.phone.replace(/\D/g, "");
    if (rawDigits.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!regForm.email.trim() || !regForm.email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    const pwdStrength = checkPasswordStrength(regForm.password);
    if (!pwdStrength.isValid) {
      toast.error("Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special symbol (min. 6 chars).");
      return;
    }
    if (regForm.password !== regForm.confirmPassword) {
      toast.error("Passwords do not match. Please check and re-enter.");
      return;
    }

    setSendingOtp(true);
    try {
      const payload = {
        first_name: regForm.firstName.trim(),
        last_name: regForm.lastName.trim(),
        phone: rawDigits.slice(-10),
        email: regForm.email.trim().toLowerCase(),
        password: regForm.password,
        confirm_password: regForm.confirmPassword,
      };
      const res = await api.post("/auth/register-send-otp", payload);
      setRegOtpSent(true);
      setRegCountdown(30);
      const mobileSent = Boolean(res.data?.mobile_otp_sent || res.data?.whatsapp_sent || res.data?.sms_sent);
      const emailSent = Boolean(res.data?.email_sent ?? true);
      const channels = { email: emailSent, mobile: mobileSent };
      setRegOtpChannels(channels);

      savePendingAuthSession({
        type: "retail_register",
        regForm: {
          firstName: regForm.firstName.trim(),
          lastName: regForm.lastName.trim(),
          phone: rawDigits.slice(-10),
          email: regForm.email.trim().toLowerCase(),
          password: regForm.password,
          confirmPassword: regForm.confirmPassword,
        },
        regOtpSent: true,
        regOtpChannels: channels,
      });

      toast.success(res.data?.message || (mobileSent ? "Verification code sent to your email and mobile number!" : "Verification code sent to your email!"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSendingOtp(false);
    }
  };

  // -----------------------------
  // B. Handle Register: Verify OTP & Create Account
  // -----------------------------
  const submitRegisterVerify = async (e) => {
    e.preventDefault();
    if (!regOtpCode || regOtpCode.trim().length !== 6) {
      toast.error("Please enter the 6-digit verification code");
      return;
    }

    setLoading(true);
    try {
      const rawDigits = regForm.phone.replace(/\D/g, "");
      const payload = {
        first_name: regForm.firstName.trim(),
        last_name: regForm.lastName.trim(),
        phone: rawDigits.slice(-10),
        email: regForm.email.trim().toLowerCase(),
        password: regForm.password,
        otp: regOtpCode.trim(),
      };
      const res = await api.post("/auth/register-verify-otp", payload);
      clearPendingAuthSession();
      onAuthSuccess(res.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSendForgotOtp = async (e) => {
    e?.preventDefault();
    const identifier = forgotIdentifier.trim();
    if (!identifier) {
      toast.error("Please enter your registered email address.");
      return;
    }
    const cleanDigits = identifier.replace(/\D/g, "");
    if (!identifier.includes("@") && cleanDigits.length >= 10) {
      toast.error("Password reset via mobile number is disabled. Please enter your registered email address.");
      return;
    }
    if (!identifier.includes("@")) {
      toast.error("Please enter a valid registered email address.");
      return;
    }

    setSendingOtp(true);
    try {
      const res = await api.post("/auth/forgot-password-send-otp", {
        identifier: identifier,
      });
      setForgotOtpSent(true);
      setForgotCountdown(30);
      const mobileSent = Boolean(res.data?.mobile_otp_sent || res.data?.whatsapp_sent || res.data?.sms_sent);
      const maskedInfo = {
        email: res.data?.masked_email,
        phone: mobileSent ? res.data?.masked_phone : null,
      };
      setForgotMaskedInfo(maskedInfo);

      savePendingAuthSession({
        type: "forgot_password",
        forgotIdentifier: identifier,
        forgotOtpSent: true,
        forgotMaskedInfo: maskedInfo,
      });

      toast.success(res.data?.message || "Password reset code sent!");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSendingOtp(false);
    }
  };

  // -----------------------------
  // C. Handle Forgot Password: Verify & Reset
  // -----------------------------
  const submitForgotReset = async (e) => {
    e.preventDefault();
    if (!forgotOtpCode || forgotOtpCode.trim().length !== 6) {
      toast.error("Please enter the 6-digit reset code");
      return;
    }
    const resetStrength = checkPasswordStrength(forgotNewPassword);
    if (!resetStrength.isValid) {
      toast.error("New password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special symbol (min. 6 chars).");
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      toast.error("Passwords do not match. Please re-enter your new password.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        identifier: forgotIdentifier.trim(),
        otp: forgotOtpCode.trim(),
        new_password: forgotNewPassword,
        confirm_password: forgotConfirmPassword,
      };
      const res = await api.post("/auth/forgot-password-verify-reset", payload);
      clearPendingAuthSession();
      toast.success("Password reset successfully!");
      onAuthSuccess(res.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 w-full min-w-0" data-testid="auth-form">
      {/* 1. Header Segmented Toggle (Sign In vs Create Account) - Hidden during Forgot Password */}
      {mode !== "forgot_password" ? (
        <div className="flex bg-stone-100 p-1 rounded-xl border border-stone-200" data-testid="auth-mode-toggle-pill">
          <button
            type="button"
            data-testid="auth-mode-signin-btn"
            onClick={() => handleModeChange("login")}
            className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer ${mode === "login"
              ? "bg-white text-[#0a2140] shadow-sm"
              : "text-stone-500 hover:text-stone-900"
              }`}
          >
            Sign In
          </button>
          <button
            type="button"
            data-testid="auth-mode-signup-btn"
            onClick={() => handleModeChange("register")}
            className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer ${mode === "register"
              ? "bg-white text-[#0a2140] shadow-sm"
              : "text-stone-500 hover:text-stone-900"
              }`}
          >
            Create Account
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between pb-1 border-b border-stone-100">
          <button
            type="button"
            onClick={() => handleModeChange("login")}
            className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 font-semibold cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back to Sign In</span>
          </button>
          <span className="text-xs font-bold text-stone-800">Password Recovery</span>
        </div>
      )}

      {/* ========================================================= */}
      {/* VIEW 1: SIMPLE SIGN IN (2 INPUTS: IDENTIFIER + PASSWORD)  */}
      {/* ========================================================= */}
      {mode === "login" && (
        <form onSubmit={submitLogin} className="space-y-3.5 w-full min-w-0" data-testid="auth-login-form">
          {/* Input 1: Registered Email Address */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">
              Email Address *
            </label>
            <div className="relative flex items-center">
              <Mail className="w-4 h-4 text-stone-400 absolute left-3.5 pointer-events-none" />
              <input
                data-testid="auth-identifier-input"
                type="text"
                required
                value={loginForm.identifier}
                onChange={(e) => setLoginForm({ ...loginForm, identifier: e.target.value })}
                placeholder="Enter your registered email"
                className={`${inputCls} pl-10`}
                autoFocus
              />
            </div>
          </div>

          {/* Input 2: Password */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-stone-700">Password</label>
              <button
                type="button"
                data-testid="auth-forgot-password-link"
                onClick={() => setMode("forgot_password")}
                className="text-xs text-[#B8860B] hover:underline font-semibold cursor-pointer"
              >
                Forgot Password?
              </button>
            </div>
            <div className="relative flex items-center">
              <Lock className="w-4 h-4 text-stone-400 absolute left-3.5 pointer-events-none" />
              <input
                data-testid="auth-password-input"
                type={showLoginPassword ? "text" : "password"}
                required
                minLength={6}
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Enter Password"
                className={`${inputCls} pl-10 pr-10`}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowLoginPassword(!showLoginPassword)}
                aria-label={showLoginPassword ? "Hide password" : "Show password"}
                className="absolute right-3 text-stone-400 hover:text-stone-700 transition-colors p-1 cursor-pointer"
              >
                {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-[#0a2140] text-white hover:bg-[#183462] rounded-xl h-11 font-bold text-sm shadow-md transition-all mt-2 cursor-pointer disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Sign In
          </Button>
        </form>
      )}

      {/* ========================================================= */}
      {/* VIEW 2: CREATE ACCOUNT (DUAL-CHANNEL SINGLE OTP VERIFICATION) */}
      {/* ========================================================= */}
      {mode === "register" && !regOtpSent && (
        <form onSubmit={handleSendRegisterOtp} className="space-y-3 w-full min-w-0" data-testid="auth-register-form">
          {/* Row 1: First Name & Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">First Name *</label>
              <div className="relative flex items-center">
                <User className="w-4 h-4 text-stone-400 absolute left-3.5 pointer-events-none" />
                <input
                  data-testid="auth-firstname-input"
                  required
                  value={regForm.firstName}
                  onChange={(e) => setRegForm({ ...regForm, firstName: e.target.value })}
                  placeholder="First name"
                  className={`${inputCls} pl-10`}
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Last Name *</label>
              <input
                data-testid="auth-lastname-input"
                required
                value={regForm.lastName}
                onChange={(e) => setRegForm({ ...regForm, lastName: e.target.value })}
                placeholder="Last name"
                className={inputCls}
              />
            </div>
          </div>

          {/* Row 2: Phone Number */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">Phone Number *</label>
            <div className="relative flex items-center">
              <span className="absolute left-3.5 text-xs font-bold text-stone-400 font-mono">+91</span>
              <input
                data-testid="auth-phone-input"
                type="tel"
                required
                maxLength={10}
                value={regForm.phone}
                onChange={(e) => setRegForm({ ...regForm, phone: e.target.value.replace(/\D/g, "") })}
                placeholder="Phone number"
                className={`${inputCls} pl-12 font-mono`}
              />
            </div>
          </div>

          {/* Row 3: Email ID */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">Email ID *</label>
            <div className="relative flex items-center">
              <Mail className="w-4 h-4 text-stone-400 absolute left-3.5 pointer-events-none" />
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={regForm.email}
                onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                placeholder="enter email"
                className={`${inputCls} pl-10 font-mono text-sm`}
              />
            </div>
          </div>

          {/* Row 4: Password & Confirm Password */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Password *</label>
              <div className="relative flex items-center">
                <Lock className="w-4 h-4 text-stone-400 absolute left-3.5 pointer-events-none" />
                <input
                  data-testid="auth-reg-password-input"
                  type={showRegPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={regForm.password}
                  onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                  placeholder="Min. 6 chars"
                  className={`${inputCls} pl-10 pr-9`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowRegPassword(!showRegPassword)}
                  className="absolute right-2.5 text-stone-400 hover:text-stone-700 p-1 cursor-pointer"
                >
                  {showRegPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Confirm Password *</label>
              <div className="relative flex items-center">
                <Lock className="w-4 h-4 text-stone-400 absolute left-3.5 pointer-events-none" />
                <input
                  data-testid="auth-reg-confirm-password-input"
                  type={showRegConfirmPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={regForm.confirmPassword}
                  onChange={(e) => setRegForm({ ...regForm, confirmPassword: e.target.value })}
                  placeholder="Confirm password"
                  className={`${inputCls} pl-10 pr-9`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowRegConfirmPassword(!showRegConfirmPassword)}
                  className="absolute right-2.5 text-stone-400 hover:text-stone-700 p-1 cursor-pointer"
                >
                  {showRegConfirmPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>

          <PasswordRequirements password={regForm.password} />

          <div className="pt-1">
            <Button
              data-testid="auth-register-send-otp-btn"
              type="submit"
              disabled={sendingOtp}
              className="w-full bg-[#0a2140] text-white hover:bg-[#183462] rounded-xl h-11 font-bold text-sm shadow-md transition-all cursor-pointer disabled:opacity-60"
            >
              {sendingOtp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Create Account & Get Verification Code
            </Button>
          </div>
        </form>
      )}

      {/* ========================================================= */}
      {/* VIEW 2 STEP 2: REGISTRATION OTP VERIFICATION (SINGLE INPUT) */}
      {/* ========================================================= */}
      {mode === "register" && regOtpSent && (
        <form onSubmit={submitRegisterVerify} className="space-y-4 w-full min-w-0" data-testid="auth-register-otp-form">
          {/* Notification banner indicating simultaneous dual dispatch */}
          <div className="p-3.5 bg-gradient-to-r from-blue-50 to-emerald-50 border border-blue-200/80 rounded-xl space-y-1.5 text-xs text-stone-800">
            <div className="flex items-center gap-2 font-bold text-[#0a2140]">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Verification Code Sent!</span>
            </div>
            <p className="text-[11px] text-stone-600 leading-relaxed">
              {regOtpChannels.mobile
                ? "We have sent the same 6-digit verification code to:"
                : "We have sent the 6-digit verification code to your registered email:"}
            </p>
            <div className="space-y-1 font-mono text-[11px] font-semibold text-stone-900 bg-white/70 p-2 rounded-lg border border-stone-200/60">
              <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span className="truncate">{regForm.email}</span>
              </div>
              {regOtpChannels.mobile && (
                <div className="flex items-center gap-1.5">
                  <FaWhatsapp className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span>+91 {regForm.phone.replace(/\D/g, "").slice(-10)}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-stone-700">Enter 6-Digit OTP Code</label>
              {regCountdown > 0 ? (
                <span className="text-[11px] text-stone-400 font-medium font-mono">Resend in {regCountdown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={handleSendRegisterOtp}
                  className="text-[11px] text-[#B8860B] hover:underline font-bold inline-flex items-center gap-1 cursor-pointer"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Resend Code</span>
                </button>
              )}
            </div>
            <input
              data-testid="auth-register-otp-input"
              type="text"
              maxLength={6}
              required
              value={regOtpCode}
              onChange={(e) => setRegOtpCode(e.target.value.replace(/\D/g, ""))}
              placeholder="• • • • • •"
              className={`${inputCls} text-center font-mono text-base tracking-[0.3em] font-bold`}
              autoFocus
            />
            <p className="text-[10px] text-stone-400 mt-1 text-center">
              ⏱ Valid for 5 minutes. Enter the code received on {regOtpChannels.mobile ? "either Email or WhatsApp/SMS" : "your Email"}.
            </p>
          </div>

          <div className="space-y-2 pt-1">
            <Button
              data-testid="auth-register-verify-btn"
              type="submit"
              disabled={loading || regOtpCode.length !== 6}
              className="w-full bg-[#0a2140] text-white hover:bg-[#183462] rounded-xl h-11 font-bold text-sm shadow-md transition-all cursor-pointer disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Verify & Complete Registration
            </Button>

            <button
              type="button"
              onClick={() => {
                setRegOtpSent(false);
                setRegOtpCode("");
                savePendingAuthSession({
                  type: "retail_register",
                  regForm,
                  regOtpSent: false,
                  regOtpChannels,
                });
              }}
              className="w-full py-1 text-xs text-stone-500 hover:text-stone-800 underline font-medium text-center cursor-pointer"
            >
              ← Edit details or fix mobile/email
            </button>
          </div>
        </form>
      )}

      {/* ========================================================= */}
      {/* VIEW 3: FORGOT PASSWORD (OTP BASED PASSWORD RECOVERY)      */}
      {/* ========================================================= */}
      {mode === "forgot_password" && !forgotOtpSent && (
        <form onSubmit={handleSendForgotOtp} className="space-y-3.5 w-full min-w-0" data-testid="auth-forgot-password-form">
          {/* <div className="text-xs text-stone-600 leading-relaxed bg-amber-50/70 border border-amber-200/80 rounded-xl p-3">
            Enter your registered <strong>Email ID</strong> or <strong>10-digit Mobile Number</strong> to receive a password reset code.
          </div> */}

          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">Registered Email Address *</label>
            <div className="relative flex items-center">
              <Mail className="w-4 h-4 text-stone-400 absolute left-3.5 pointer-events-none" />
              <input
                data-testid="auth-forgot-identifier-input"
                type="email"
                required
                value={forgotIdentifier}
                onChange={(e) => setForgotIdentifier(e.target.value)}
                placeholder="Enter your registered email"
                className={`${inputCls} pl-10`}
                autoFocus
              />
            </div>
          </div>

          <Button
            data-testid="auth-forgot-send-btn"
            type="submit"
            disabled={sendingOtp || !forgotIdentifier.trim()}
            className="w-full bg-[#0a2140] text-white hover:bg-[#183462] rounded-xl h-11 font-bold text-sm shadow-md transition-all cursor-pointer disabled:opacity-60"
          >
            {sendingOtp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Send Password Reset Code
          </Button>
        </form>
      )}

      {/* ========================================================= */}
      {/* VIEW 3 STEP 2: FORGOT PASSWORD OTP & NEW PASSWORD INPUT    */}
      {/* ========================================================= */}
      {mode === "forgot_password" && forgotOtpSent && (
        <form onSubmit={submitForgotReset} className="space-y-3.5 w-full min-w-0" data-testid="auth-forgot-reset-form">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-stone-800 space-y-1">
            <span className="font-bold text-[#0a2140] block">Password Reset Code Dispatched</span>
            <p className="text-[11px] text-stone-600">
              Code sent to {forgotMaskedInfo.email && <strong>{forgotMaskedInfo.email}</strong>}
              {forgotMaskedInfo.email && forgotMaskedInfo.phone && " and "}
              {forgotMaskedInfo.phone && <strong>{forgotMaskedInfo.phone}</strong>}.
            </p>
          </div>

          {/* 6-Digit OTP Code */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-stone-700">Enter 6-Digit Reset Code</label>
              {forgotCountdown > 0 ? (
                <span className="text-[11px] text-stone-400 font-mono">Resend in {forgotCountdown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={handleSendForgotOtp}
                  className="text-[11px] text-[#B8860B] hover:underline font-bold cursor-pointer"
                >
                  Resend Code
                </button>
              )}
            </div>
            <input
              data-testid="auth-forgot-otp-input"
              type="text"
              maxLength={6}
              required
              value={forgotOtpCode}
              onChange={(e) => setForgotOtpCode(e.target.value.replace(/\D/g, ""))}
              placeholder="• • • • • •"
              className={`${inputCls} text-center font-mono text-base tracking-[0.3em] font-bold`}
              autoFocus
            />
          </div>

          {/* New Password */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">New Password (min. 6 chars)</label>
            <div className="relative flex items-center">
              <Lock className="w-4 h-4 text-stone-400 absolute left-3.5 pointer-events-none" />
              <input
                data-testid="auth-forgot-new-password-input"
                type={showForgotNewPassword ? "text" : "password"}
                required
                minLength={6}
                value={forgotNewPassword}
                onChange={(e) => setForgotNewPassword(e.target.value)}
                placeholder="Enter your new password"
                className={`${inputCls} pl-10 pr-10`}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowForgotNewPassword(!showForgotNewPassword)}
                className="absolute right-3 text-stone-400 hover:text-stone-700 p-1 cursor-pointer"
              >
                {showForgotNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Confirm New Password */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">Confirm New Password</label>
            <div className="relative flex items-center">
              <Lock className="w-4 h-4 text-stone-400 absolute left-3.5 pointer-events-none" />
              <input
                data-testid="auth-forgot-confirm-password-input"
                type={showForgotConfirmPassword ? "text" : "password"}
                required
                minLength={6}
                value={forgotConfirmPassword}
                onChange={(e) => setForgotConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                className={`${inputCls} pl-10 pr-10`}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowForgotConfirmPassword(!showForgotConfirmPassword)}
                className="absolute right-3 text-stone-400 hover:text-stone-700 p-1 cursor-pointer"
              >
                {showForgotConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <PasswordRequirements password={forgotNewPassword} />

          <div className="space-y-2 pt-1">
            <Button
              data-testid="auth-forgot-verify-btn"
              type="submit"
              disabled={loading || forgotOtpCode.length !== 6}
              className="w-full bg-[#0a2140] text-white hover:bg-[#183462] rounded-xl h-11 font-bold text-sm shadow-md transition-all cursor-pointer disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Reset Password & Sign In
            </Button>

            <button
              type="button"
              onClick={() => {
                setForgotOtpSent(false);
                setForgotOtpCode("");
              }}
              className="w-full py-1 text-xs text-stone-500 hover:text-stone-800 underline font-medium text-center cursor-pointer"
            >
              ← Re-enter email / phone number
            </button>
          </div>
        </form>
      )}

      {/* Clean Wholesale B2B Footer */}
      {onSwitchToB2B && (
        <div className="pt-3 border-t border-stone-100 text-center">
          <p className="text-xs text-stone-500">
            Jewellery store or wholesale buyer?{" "}
            <button
              type="button"
              onClick={onSwitchToB2B}
              className="text-[#0a2140] font-bold hover:underline cursor-pointer"
            >
              Wholesale Registration
            </button>
          </p>
        </div>
      )}
    </div>
  );
};
